import React, { useCallback, useEffect, useRef, useState } from "react";

export default function MarketingVideoOverlay({ item, onClose, onClick }) {
  const { creative } = item;
  const videoRef = useRef(null);
  const [canClose, setCanClose] = useState(creative.skipAfterSeconds <= 0);
  const showCta = !!creative.target?.href && !!creative.ctaLabel;
  const showSkip = false;
  const hasTarget = !!creative.target?.href;

  const handleMediaKeyDown = (event) => {
    if (!hasTarget) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick(item);
    }
  };

  useEffect(() => {
    if (creative.skipAfterSeconds <= 0) return undefined;

    const timer = setTimeout(
      () => setCanClose(true),
      creative.skipAfterSeconds * 1000
    );

    return () => clearTimeout(timer);
  }, [creative.skipAfterSeconds]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const requestClose = useCallback(
    (reason) => {
      if (reason !== "complete" && (!creative.dismissible || !canClose)) {
        return;
      }

      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }

      onClose(reason);
    },
    [canClose, creative.dismissible, onClose]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        requestClose("escape");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [requestClose]);

  return (
    <div
      className="marketing-overlay marketing-video-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={creative.asset.alt || creative.title || "Video marketing"}
    >
      <button
        type="button"
        className="marketing-overlay-backdrop"
        aria-label="Cerrar anuncio"
        disabled={!creative.dismissible || !canClose}
        onClick={() => requestClose("backdrop")}
      />
      <div className="marketing-video-panel">
        {creative.dismissible && canClose ? (
          <button
            type="button"
            className="marketing-close-button marketing-overlay-close"
            aria-label="Cerrar anuncio"
            onClick={() => requestClose("close_button")}
          >
            &times;
          </button>
        ) : null}
        <video
          ref={videoRef}
          src={creative.asset.src}
          poster={creative.asset.poster}
          autoPlay={creative.autoplay}
          muted={creative.muted}
          playsInline={creative.playsInline}
          controls={creative.controls}
          role={hasTarget ? "button" : undefined}
          tabIndex={hasTarget ? 0 : undefined}
          aria-label={
            hasTarget ? creative.asset.alt || "Abrir anuncio" : undefined
          }
          onClick={hasTarget ? () => onClick(item) : undefined}
          onKeyDown={handleMediaKeyDown}
          className={[
            "marketing-video-player",
            hasTarget ? "marketing-video-player--clickable" : "",
          ].join(" ")}
          onEnded={() => {
            if (creative.closeOnEnd) {
              requestClose("complete");
            } else {
              setCanClose(true);
            }
          }}
        />
        {(showCta || showSkip) ? (
          <div className="marketing-video-actions">
            {showCta ? (
              <button
                type="button"
                className="marketing-cta-button"
                onClick={() => onClick(item)}
              >
                {creative.ctaLabel}
              </button>
            ) : null}
            {showSkip ? (
              <button
                type="button"
                className="marketing-skip-button"
                onClick={() => requestClose("skip_button")}
              >
                Omitir
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
